import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Button,
  ButtonGroup,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Spinner,
  customColors,
} from '@apollo/orbit';
import { Select } from '@apollo/space-kit/Select';
import React, { useCallback, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';

import { CursorProvider } from 'src/components/charts/helpers/Cursor';
import {
  LineChart,
  LineChartStackConfig,
} from 'src/components/charts/LineChart';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { Typeahead } from 'src/components/common/typeahead/Typeahead';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useQueryParam } from 'src/hooks/useQueryParam';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import Config from 'src/lib/config';
import { date, getRelativeDateBounds } from 'src/lib/date/date';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { AlertsChartStats_service_stats_queryStats } from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';

const ALERTS_TRIGGER_INPUTS_QUERY = gql<
  GraphQLTypes.PerformanceAlertsTriggerInputs,
  GraphQLTypes.PerformanceAlertsTriggerInputsVariables
>`
  query PerformanceAlertsTriggerInputs(
    $serviceId: ID!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
  ) {
    service(id: $serviceId) {
      id
      variants {
        id
        name
      }
      stats: statsWindow(from: $timeFrom, to: $timeTo) {
        queryStats {
          group: groupBy {
            queryName
          }
        }
      }
      channels {
        id
        name
        ... on SlackChannel {
          url
        }
        ... on PagerDutyChannel {
          routingKey
        }
      }
      roles {
        canQueryPrivateInfo
        canManageIntegrations
      }
    }
  }
`;

export const QUERY_TRIGGER_FRAGMENT = gql`
  fragment QueryTriggerFragment on QueryTrigger {
    id
    scope
    operationNames
    excludedOperationNames
    metric
    percentile
    threshold
    comparisonOperator
    window
    enabled
    variant
    state {
      triggered
      evaluatedAt
      lastTriggeredAt
    }
    channels {
      id
      name
      ... on PagerDutyChannel {
        __typename
      }
      ... on SlackChannel {
        __typename
      }
      ... on WebhookChannel {
        __typename
      }
    }
  }
`;

export const PERFORMANCE_ALERTS_QUERY = gql<
  GraphQLTypes.PerformanceAlertsTriggers,
  GraphQLTypes.PerformanceAlertsTriggersVariables
>`
  query PerformanceAlertsTriggers($serviceId: ID!) {
    service(id: $serviceId) {
      id
      queryTriggers {
        id
        ...QueryTriggerFragment
      }
    }
  }
  ${QUERY_TRIGGER_FRAGMENT}
`;

export const UPSERT_QUERY_TRIGGER_MUTATION = gql<
  GraphQLTypes.UpsertQueryTriggerMutation,
  GraphQLTypes.UpsertQueryTriggerMutationVariables
>`
  mutation UpsertQueryTriggerMutation(
    $serviceId: ID!
    $triggerId: ID
    $trigger: QueryTriggerInput!
  ) {
    service(id: $serviceId) {
      upsertQueryTrigger(id: $triggerId, trigger: $trigger) {
        id
        ...QueryTriggerFragment
      }
    }
  }
  ${QUERY_TRIGGER_FRAGMENT}
`;

const ALERTS_CHART_STATS_QUERY = gql<
  GraphQLTypes.AlertsChartStats,
  GraphQLTypes.AlertsChartStatsVariables
>`
  query AlertsChartStats(
    $serviceId: ID!
    $filter: ServiceQueryStatsFilter
    $resolution: Resolution!
    $timeFrom: Timestamp!
    $timeTo: Timestamp!
  ) {
    service(id: $serviceId) {
      id
      stats: statsWindow(
        from: $timeFrom
        to: $timeTo
        resolution: $resolution
      ) {
        queryStats(filter: $filter) {
          timestamp
          metrics {
            requestsWithErrorsCount
            uncachedRequestsCount
            cachedRequestsCount
            totalLatencyHistogram {
              totalCount
              serviceTimeP99: durationMs(percentile: 0.99)
              serviceTimeP95: durationMs(percentile: 0.95)
              serviceTimeP50: durationMs(percentile: 0.50)
            }
          }
        }
      }
    }
  }
`;

const triggers = [
  'p50 Service Time',
  'p95 Service Time',
  'p99 Service Time',
  'Error Rate',
  'Error Percentage',
  'Request Rate',
] as const;
type Trigger = (typeof triggers)[number];

const printTriggerValue = (metric: Trigger, value: number) => {
  return metric === 'Error Rate' || metric === 'Request Rate'
    ? format.requests(value)
    : metric === 'Error Percentage'
      ? format.percent(value)
      : metric === 'p50 Service Time' ||
          metric === 'p95 Service Time' ||
          metric === 'p99 Service Time'
        ? format.ms(value)
        : assertUnreachable(metric);
};

/**
 * We hard code resolution to be 5 minutes because that's the sliding window
 * on which the performance alerts operate and trigger, and the default
 * resolution for 1 day is 15 minutes.
 *
 * This leads to misleading scenarios where a user might set up a trigger
 * threshold for when XXX > 50, but see a value of 60 in their chart, and
 * wonder why the 60 did not trigger their alert. The answer is because the 60
 * is a summary of a 15 minute window, and on 5 minute increments, no value
 * exceeded 50.
 */
const resolution = GraphQLTypes.Resolution.R5M;

function getTriggerValue({
  trigger,
  metrics,
  timestamp,
}: {
  trigger: Trigger;
  metrics: AlertsChartStats_service_stats_queryStats['metrics'];
  timestamp: string | number;
}) {
  return (
    (trigger === 'Error Rate'
      ? metrics.requestsWithErrorsCount /
        date.getMinDifference(
          timestamp,
          date.incrementByResolution(timestamp, resolution),
        )
      : trigger === 'Request Rate'
        ? metrics.totalLatencyHistogram.totalCount /
          date.getMinDifference(
            timestamp,
            date.incrementByResolution(timestamp, resolution),
          )
        : trigger === 'Error Percentage'
          ? metrics.requestsWithErrorsCount /
            metrics.totalLatencyHistogram.totalCount
          : trigger === 'p50 Service Time'
            ? metrics.totalLatencyHistogram.serviceTimeP50
            : trigger === 'p95 Service Time'
              ? metrics.totalLatencyHistogram.serviceTimeP95
              : trigger === 'p99 Service Time'
                ? metrics.totalLatencyHistogram.serviceTimeP99
                : assertUnreachable(trigger)) ?? 0
  );
}

export const windowToCountMultiplier = {
  UNRECOGNIZED: 0,
  ONE_MINUTE: 1,
  FIVE_MINUTES: 5,
  FIFTEEN_MINUTES: 15,
};

export const AlertUpsertModal = ({
  isOpen,
  onClose,
  graphId,
  queryTriggers = [],
}: {
  isOpen: boolean;
  onClose: () => void;
  graphId: string;
  queryTriggers?: GraphQLTypes.PerformanceAlertsTriggers_service_queryTriggers[];
}) => {
  const history = useHistory();
  const location = useLocation();

  const triggerId = useQueryParam('triggerId');
  // are we editing a trigger or creating a new one?
  const eTrigger = queryTriggers.find((t) => t.id === triggerId);

  const [selectedTrigger, setSelectedTrigger] = useState(
    eTrigger
      ? eTrigger.metric === GraphQLTypes.QueryTriggerMetric.ERROR_PERCENTAGE
        ? 'Error Percentage'
        : eTrigger.metric === GraphQLTypes.QueryTriggerMetric.ERROR_COUNT
          ? 'Error Rate'
          : eTrigger.metric === GraphQLTypes.QueryTriggerMetric.REQUEST_COUNT
            ? 'Request Rate'
            : eTrigger.metric ===
                GraphQLTypes.QueryTriggerMetric.REQUEST_SERVICE_TIME
              ? eTrigger.percentile === 0.5
                ? 'p50 Service Time'
                : eTrigger.percentile === 0.95
                  ? 'p95 Service Time'
                  : 'p99 Service Time'
              : assertUnreachable(eTrigger.metric)
      : ('Error Rate' as Trigger),
  );
  const [selectedTriggerValue, setSelectedTriggerValue] = useState(
    eTrigger?.threshold
      ? eTrigger?.metric === GraphQLTypes.QueryTriggerMetric.ERROR_PERCENTAGE
        ? eTrigger.threshold * 100
        : eTrigger?.metric === GraphQLTypes.QueryTriggerMetric.ERROR_COUNT ||
            eTrigger?.metric === GraphQLTypes.QueryTriggerMetric.REQUEST_COUNT
          ? // TODO: remove this disabling rule for eslint v8 migration
            // eslint-disable-next-line no-unsafe-optional-chaining
            eTrigger?.threshold /
            windowToCountMultiplier[
              GraphQLTypes.QueryTriggerWindow.FIVE_MINUTES
            ]
          : eTrigger?.threshold
      : 0,
  );
  const [selectedTriggerComparison, setSelectedTriggerComparison] = useState(
    eTrigger?.comparisonOperator ??
      GraphQLTypes.ComparisonOperator.GREATER_THAN,
  );

  const [selectedVariant, setSelectedVariant] = useState(
    eTrigger?.variant ?? 'All variants',
  );
  const [selectedOperationName, setSelectedOperationName] = useState(
    eTrigger?.operationNames[0] ?? 'All operations',
  );

  const [upsertTriggerMutation, { loading: upsertLoading, error }] =
    useMutation<
      GraphQLTypes.UpsertQueryTriggerMutation,
      GraphQLTypes.UpsertQueryTriggerMutationVariables
    >(UPSERT_QUERY_TRIGGER_MUTATION, {
      refetchQueries: [
        {
          query: PERFORMANCE_ALERTS_QUERY,
          variables: {
            serviceId: graphId,
          },
        },
      ],
      awaitRefetchQueries: true,
      onCompleted: () => onClose(),
      errorPolicy: 'all',
    });

  const { timeFrom, timeTo, pollInterval } = getRelativeDateBounds('lastDay');

  const { data, loading } = useQuery<
    GraphQLTypes.PerformanceAlertsTriggerInputs,
    GraphQLTypes.PerformanceAlertsTriggerInputsVariables
  >(ALERTS_TRIGGER_INPUTS_QUERY, {
    variables: { serviceId: graphId, timeFrom, timeTo },
    pollInterval,
  });

  const channels =
    data?.service?.channels?.filter(
      (c) =>
        c.__typename === 'PagerDutyChannel' || c.__typename === 'SlackChannel',
    ) ?? [];
  const [selectedChannel, setSelectedChannel] = useState(
    eTrigger?.channels[0]?.id ?? undefined,
  );

  const { data: chartData, loading: chartLoading } = useQuery<
    GraphQLTypes.AlertsChartStats,
    GraphQLTypes.AlertsChartStatsVariables
  >(ALERTS_CHART_STATS_QUERY, {
    variables: {
      serviceId: graphId,
      resolution,
      timeFrom,
      timeTo,
      filter: {
        queryName:
          selectedOperationName === 'All operations'
            ? undefined
            : selectedOperationName,
        schemaTag:
          selectedVariant === 'All variants' ? undefined : selectedVariant,
      },
    },
    pollInterval,
  });

  const minorLineChartStacks = useMemo(
    (): LineChartStackConfig<AlertsChartStats_service_stats_queryStats>[] => [
      {
        primaryColor: customColors.flowchart.d,
        data: chartData?.service?.stats?.queryStats ?? [],
        label: selectedTrigger,
        unit:
          selectedTrigger === 'Error Rate' || selectedTrigger === 'Request Rate'
            ? '/min'
            : undefined,
        getValue: ({
          metrics,
          timestamp,
        }: AlertsChartStats_service_stats_queryStats) => ({
          timestamp,
          value: getTriggerValue({
            trigger: selectedTrigger,
            metrics,
            timestamp,
          }),
        }),
      },
    ],
    [chartData?.service?.stats?.queryStats, selectedTrigger],
  );

  const lineChartStacks = useMemo(
    (): LineChartStackConfig<AlertsChartStats_service_stats_queryStats>[] => [
      {
        primaryColor: customColors.code.a,
        label: 'Request Rate',
        unit: '/min',
        data: chartData?.service?.stats?.queryStats ?? [],
        getValue: ({
          metrics,
          timestamp,
        }: AlertsChartStats_service_stats_queryStats) => ({
          timestamp,
          value: getTriggerValue({
            trigger: 'Request Rate',
            metrics,
            timestamp,
          }),
        }),
      },
    ],
    [chartData?.service?.stats?.queryStats],
  );

  const formatValue = useCallback(
    (axis: 'major' | 'minor', value: number) =>
      printTriggerValue(
        axis === 'major' ? 'Request Rate' : selectedTrigger,
        value,
      ),
    [selectedTrigger],
  );
  return (
    <Modal isOpen={isOpen} size="3xl" onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Create a new performance alert</ModalTitle>
        </ModalHeader>
        <ModalBody>
          <div className="flex gap-4">
            <div className="flex-1">
              <div className="font-semibold">Variant:</div>
              <Select
                className="w-full"
                value={selectedVariant}
                disabled={loading}
                onChange={(e) => {
                  setSelectedVariant(e.target.value);
                }}
              >
                <optgroup key="all">
                  <option value="All variants" key="All variants">
                    All variants
                  </option>
                </optgroup>
                <optgroup key="options">
                  {data?.service?.variants.map((v) => (
                    <option value={v.name} key={v.id}>
                      {v.name}
                    </option>
                  ))}
                </optgroup>
              </Select>
            </div>
            <div className="flex-1">
              <div className="font-semibold">Channel:</div>
              <Select
                disabled={loading}
                className="w-full"
                value={selectedChannel}
                onChange={(e) => {
                  if (e.target.value === 'new-slack-channel') {
                    history.push(
                      setOverlayVisibleLocation({
                        location,
                        overlayName: Config.modals.createSlackChannel,
                      }),
                    );
                  } else if (e.target.value === 'new-pagerduty-channel') {
                    history.push(
                      setOverlayVisibleLocation({
                        location,
                        overlayName: Config.modals.createPagerDutyChannel,
                      }),
                    );
                  } else {
                    setSelectedChannel(e.target.value);
                  }
                }}
                renderTriggerNode={(selected) =>
                  channels.find((c) => c.id === selected?.value)?.name ??
                  'No channel'
                }
              >
                <optgroup label="Configured channels" key="configured">
                  {channels.map((channel) => (
                    <option
                      value={channel.id}
                      key={`channel-option-${channel.id}`}
                    >
                      {channel.name || channel.id}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="New channel" key="new">
                  <option value="new-slack-channel" key="new-slack">
                    New Slack Channel
                  </option>
                  <option value="new-pagerduty-channel" key="new-pd">
                    New PagerDuty Channel
                  </option>
                </optgroup>
              </Select>
            </div>
            <Tooltip
              disabled={!(loading || !!eTrigger)}
              label="The operation name filter can't be changed after an alert is created"
            >
              <div className="flex-1">
                <div className="font-semibold">Operation Name:</div>
                <Typeahead
                  disabled={loading || !!eTrigger}
                  className="w-full"
                  defaultValue={selectedOperationName}
                  options={[
                    'All operations',
                    ...(data?.service?.stats?.queryStats ?? [])
                      .map(
                        ({ group: { queryName } }) =>
                          queryName || 'Operation name not available',
                      )
                      .sort(),
                  ]}
                  onSelected={setSelectedOperationName}
                  renderToString={(o) => o}
                  renderDividerAfterCurrNode={(o) => o === 'All operations'}
                  renderToListItem={(o) => o}
                  getId={(o) => o}
                  getSearchString={(o) => o}
                />
              </div>
            </Tooltip>
          </div>

          <div className="flex gap-4">
            <div>
              <div className="font-semibold">Trigger:</div>
              <Select
                className="mr-4"
                onChange={(event) => {
                  setSelectedTrigger(event.target.value as Trigger);
                }}
                disabled={loading}
                value={selectedTrigger}
              >
                {triggers.map((t) => (
                  <option value={t} key={t}>
                    {t}
                  </option>
                ))}
              </Select>
              <Tooltip
                disabled={selectedTrigger === 'Request Rate'}
                label={`You can only change this for the "Request Rate" trigger type`}
              >
                <span>
                  <Select
                    disabled={selectedTrigger !== 'Request Rate'}
                    value={selectedTriggerComparison}
                    onChange={(e) => {
                      setSelectedTriggerComparison(
                        e.target.value as GraphQLTypes.ComparisonOperator,
                      );
                    }}
                  >
                    <option
                      value={GraphQLTypes.ComparisonOperator.GREATER_THAN}
                      key="greater"
                    >
                      greater than
                    </option>
                    <option
                      value={GraphQLTypes.ComparisonOperator.LESS_THAN}
                      key="less"
                    >
                      less than
                    </option>
                  </Select>
                </span>
              </Tooltip>
            </div>
            <div>
              <div className="font-semibold">Trigger Value:</div>
              <div className="relative flex items-center">
                <Input
                  isDisabled={loading}
                  type="number"
                  value={selectedTriggerValue}
                  onChange={(e) => {
                    setSelectedTriggerValue(parseInt(e.target.value, 10));
                  }}
                />
                <span className="ml-2">
                  {selectedTrigger === 'Error Percentage'
                    ? '%'
                    : selectedTrigger === 'Error Rate' ||
                        selectedTrigger === 'Request Rate'
                      ? 'rpm'
                      : selectedTrigger === 'p50 Service Time' ||
                          selectedTrigger === 'p95 Service Time' ||
                          selectedTrigger === 'p99 Service Time'
                        ? 'ms'
                        : assertUnreachable(selectedTrigger)}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <div className="font-semibold">
              <span className="underline">{selectedTrigger}</span> for{' '}
              <span className="underline">{selectedOperationName}</span> on{' '}
              <span className="underline">{selectedVariant}</span>:
            </div>
            {chartLoading ? (
              <div className="mt-6 flex h-40 items-center justify-center">
                <Spinner size="sm" />
              </div>
            ) : (
              selectedTrigger && (
                <CursorProvider>
                  <div className="mb-4 flex px-10">
                    <div className="flex-1">
                      <div className="mr-2 inline-block size-3 rounded-full bg-blilet" />
                      <span>{selectedTrigger}</span>
                    </div>
                    <div className="flex-none">
                      <span>Request Rate (rpm)</span>
                      <div className="ml-2 inline-block size-3 rounded-full bg-silver-darker" />
                    </div>
                  </div>
                  <LineChart
                    stacks={lineChartStacks}
                    minorStacks={minorLineChartStacks}
                    stacked
                    className="h-40"
                    strokeWidth={1}
                    resolution={resolution}
                    comparisonLine={{
                      value:
                        selectedTrigger === 'Error Percentage'
                          ? selectedTriggerValue / 100
                          : selectedTriggerValue,
                      color: customColors.border.error,
                      label: `${printTriggerValue(
                        selectedTrigger,
                        selectedTrigger === 'Error Percentage'
                          ? selectedTriggerValue / 100
                          : selectedTriggerValue,
                      )} threshold`,
                    }}
                    formatValue={formatValue}
                  />
                </CursorProvider>
              )
            )}
          </div>
          {error && <ErrorMessage />}
        </ModalBody>
        <ModalFooter>
          <ButtonGroup>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isLoading={upsertLoading}
              loadingText={eTrigger ? 'Update' : 'Create'}
              onClick={() => {
                upsertTriggerMutation({
                  variables: {
                    serviceId: graphId,
                    triggerId: eTrigger?.id ?? undefined, // undefined for new trigger
                    trigger: {
                      metric:
                        selectedTrigger === 'Error Percentage'
                          ? GraphQLTypes.QueryTriggerMetric.ERROR_PERCENTAGE
                          : selectedTrigger === 'Error Rate'
                            ? GraphQLTypes.QueryTriggerMetric.ERROR_COUNT
                            : selectedTrigger === 'Request Rate'
                              ? GraphQLTypes.QueryTriggerMetric.REQUEST_COUNT
                              : selectedTrigger === 'p50 Service Time' ||
                                  selectedTrigger === 'p95 Service Time' ||
                                  selectedTrigger === 'p99 Service Time'
                                ? GraphQLTypes.QueryTriggerMetric
                                    .REQUEST_SERVICE_TIME
                                : assertUnreachable(selectedTrigger),
                      enabled: true,
                      channelIds: selectedChannel ? [selectedChannel] : null,
                      operationNames:
                        selectedOperationName === 'All operations'
                          ? null
                          : [selectedOperationName],
                      comparisonOperator: selectedTriggerComparison,
                      variant:
                        selectedVariant === 'All variants'
                          ? null
                          : selectedVariant,
                      window: GraphQLTypes.QueryTriggerWindow.FIVE_MINUTES,
                      scope: GraphQLTypes.QueryTriggerScope.ANY,
                      percentile:
                        selectedTrigger === 'p50 Service Time'
                          ? 0.5
                          : selectedTrigger === 'p95 Service Time'
                            ? 0.95
                            : selectedTrigger === 'p99 Service Time'
                              ? 0.99
                              : null,
                      threshold:
                        selectedTrigger === 'Error Percentage'
                          ? selectedTriggerValue / 100
                          : selectedTrigger === 'Error Rate' ||
                              selectedTrigger === 'Request Rate'
                            ? selectedTriggerValue *
                              windowToCountMultiplier[
                                GraphQLTypes.QueryTriggerWindow.FIVE_MINUTES
                              ]
                            : selectedTriggerValue,
                    },
                  },
                });
              }}
            >
              {eTrigger ? 'Update' : 'Create'}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
